import "./Form.scss";

import filterFileList from "./helpers/filterFileList";
import FileDialog from "./FileDialog";
import data from "./data";
import icons from "./icons";
import splitFileList from "./helpers/splitFileList";
import formatBytes from "./helpers/formatBytes";

/**
 * A class to wrap and extend the functionality of the given form to allow for better file uploading.
 */
export default class Form {
    /**
     * Create a new instance of the class.
     * @param {HTMLFormElement} form
     */
    constructor( form ) {
        // bind the context within internal listeners
        this.onDragOver = this.onDragOver.bind( this );
        this.onDragDrop = this.onDragDrop.bind( this );
        this.onFilesChanged = this.onFilesChanged.bind( this );

        /**
         * The HTMLFormElement this class wraps and extends the functionality of.
         * @type {HTMLFormElement}
         * @readonly
         */
        this.element = form;
        /**
         * The combined global & form level strings.
         * @type {{}}
         * @readonly
         */
        this.i18n = Object.assign( {}, data.i18n, JSON.parse( this.element.dataset.i18n ?? "{}" ) );
        /**
         *
         * @type {HTMLLabelElement}
         * @readonly
         */
        this.dropZone = this.element.querySelector( ".fg-feu-drop-zone" );
        this.dropZone?.addEventListener( "dragover", this.onDragOver );
        this.dropZone?.addEventListener( "drop", this.onDragDrop );
        /**
         * @type {HTMLInputElement}
         * @readonly
         */
        this.fileInput = this.element.querySelector( "input[name='foogallery_images[]']" );
        this.fileInput?.addEventListener( "change", this.onFilesChanged );
        /**
         *
         * @type {string[]}
         * @readonly
         */
        this.fields = ( this.element.dataset.fields?.split( " " ) ?? [] ).filter( f => typeof f === 'string' && f.length > 0 );
        /**
         *
         * @type {string[]}
         * @readonly
         */
        this.requiredFields = ( this.element.dataset.requiredFields?.split( " " ) ?? [] ).filter( f => typeof f === 'string' && f.length > 0 );
        /**
         *
         * @type {number}
         * @readonly
         */
        this.maxSize = parseInt( this.element.dataset.maxSize ?? 0 ) * 1024 * 1024;
        /**
         *
         * @type {number}
         * @readonly
         */
        this.maxImages = parseInt( this.element.dataset.maxImages ?? 0 );
        /**
         * @type {string[]}
         * @readonly
         */
        this.allowedTypes = ( this.element.dataset.allowedTypes?.split( "," ) ?? [] ).map( type => type.trim().toLowerCase() );
        /**
         *
         * @type {FileDialog|null}
         * @readonly
         */
        this.dialog = null;
        if ( this.element.classList.contains('fg-feu-show-icon') ) {
            this.dropZone?.insertAdjacentHTML( 'afterbegin', icons.upload );
        }
    }

    /**
     *
     * @type {boolean}
     * @readonly
     */
    get hasAnyFiles() {
        return ( this.fileInput?.files?.length > 0 ) ?? false;
    }

    /**
     *
     * @type {boolean}
     * @readonly
     */
    get hasSingleFile() {
        return ( this.fileInput?.files?.length === 1 ) ?? false;
    }

    /**
     *
     * @type {boolean}
     * @readonly
     */
    get hasFields() {
        return this.fields.length > 0;
    }

    /**
     *
     * @return {File[]}
     * @readonly
     */
    get files() {
        return Array.from( this.fileInput.files ?? [] );
    }

    /**
     *
     * @param {FormData} formData
     * @return {Promise<UploadResponse>}
     */
    async upload( formData ) {
        try {
            const response= await fetch( data.rest.endpoint, {
                method: "POST",
                credentials: 'same-origin',
                headers: {
                    // supply this so we have access to logged-in user details within the endpoints PHP code
                    "X-WP-Nonce": data.rest.nonce
                },
                body: formData
            } );
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                return { success: false, message: this.i18n.restBadResponse };
            }
            const result = await response.json();
            return !response.ok ? Object.assign( result, { success: false } ) : result;
        } catch ( err ) {
            return { success: false, message: this.i18n.restNetworkError, error: err };
        }
    }

    /**
     * Reset the form back to its empty state.
     */
    reset() {
        // clears any selected files
        this.fileInput.value = null;
    }

    /**
     * Checks if the `fileInput` has any files and triggers the dialog if required.
     */
    onFilesChanged() {
        if ( this.hasAnyFiles ) {
            if ( this.dialog === null ) this.dialog = new FileDialog( this );
            this.dialog.open();
        }
    }

    /**
     *
     * @param {DragEvent} event
     */
    onDragOver( event ) {
        if ( event.dataTransfer.items.length > 0 ) {
            // signal intent to handle the drop
            event.preventDefault();
        }
    }

    /**
     *
     * @param {DragEvent} event
     */
    onDragDrop( event ) {
        const dropped = filterFileList( event.dataTransfer.files, file => 
            file.type.startsWith( "image/" ) && this.isAllowedFileType( file ) 
        );
        if ( dropped.length > 0 ) {
            // if there are any images, handle the drop
            event.preventDefault();
            this.fileInput.files = dropped;
            this.onFilesChanged();
        }
    }

    /**
     * Check if the given file has an allowed extension
     * @param {File} file
     * @returns {boolean}
     */
    isAllowedFileType( file ) {
        if ( !this.allowedTypes.length ) return true;
        const extension = file.name.split( "." ).pop()?.toLowerCase();
        return extension && this.allowedTypes.includes( extension );
    }
};

//region type-defs

/**
 * @typedef {object} BadResponse
 * @property {false} success
 * @property {string} message
 */

/**
 * @typedef {object} NetworkErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {Error} error
 */

/**
 * @typedef {object} WPErrorResponse
 * @property {false} success
 * @property {string} message
 * @property {string|int} code
 * @property {object} data
 */

/**
 * @typedef {{ status: ("succeeded" | "errored" | "skipped"), name: string, message: string }} FileResult
 * @property {("succeeded" | "errored" | "skipped")} status
 * @property {string} name
 * @property {string} message
 */

/**
 * @typedef {object} SuccessResponse
 * @property {true} success
 * @property {string} message
 * @property {FileResult[]} files
 * @property {number} succeeded
 * @property {number} errored
 * @property {number} skipped
 */

/**
 * @typedef {SuccessResponse|WPErrorResponse|BadResponse|NetworkErrorResponse} UploadResponse
 */

//endregion